import { Children, type ReactNode } from 'react'

import { CONTROLS } from '@brickup/shared/locales/de/controls'
import { Badge } from './Badge'
import { cn } from './cn'
import { Tooltip } from './Tooltip'

/**
 * A set of badges side by side, wrapping into whatever width it is given.
 *
 * Pills are how the app shows a set of short names — an entry's tags, the
 * places a formula is used, the fields an import could not fill. One row for
 * all of them, so a set of six reads as one object and the gap between two
 * pills is the same wherever they are shown.
 *
 * `max` caps a row that has to stay on one line. The rest become one `+n`
 * pill, and `names` says what they are in its tooltip — a count nobody can
 * resolve is worse than a row that wraps.
 */
export function BadgeRow({
  align = 'start',
  max,
  names,
  children,
  className,
}: {
  /** `center` is for a table cell, whose column is centred as a whole. */
  align?: 'start' | 'center'
  /** How many pills the row shows before the remainder becomes a count. */
  max?: number
  /** Every pill by name, in the order they are rendered. The row takes the
   *  ones it hid off the end for the `+n` tooltip, so a caller cannot say the
   *  count and the names differently. */
  names?: readonly string[]
  children: ReactNode
  /** Only the room around the row — a set inside a sheet row needs its air. */
  className?: string
}) {
  const all = Children.toArray(children)
  const shown = max == null ? all : all.slice(0, max)
  const hidden = all.length - shown.length

  return (
    <div
      className={cn(
        'flex flex-row flex-wrap gap-1.5',
        align === 'center' && 'justify-center',
        className,
      )}
    >
      {shown}
      {hidden > 0 ? (
        <Tooltip label={(names ?? []).slice(shown.length).join(', ')}>
          <Badge size="sm" label={CONTROLS.morePills(hidden)} />
        </Tooltip>
      ) : null}
    </div>
  )
}
